/**
 * Phase 4.5: Quick Ask Prompt Builder
 *
 * Constructs contextual prompts for Quick Ask follow-up questions
 * about MECE tactics.
 */

export type QuickAskQuestionType =
  | 'why'           // "Why this recommendation?"
  | 'how'           // "How to implement?"
  | 'risks'         // "What are the risks?"
  | 'resources'     // "What resources are needed?"
  | 'timeline'      // "Expected timeline?"
  | 'custom'        // User-typed question

export interface QuickAskContext {
  tactic_id: string
  tactic_title: string
  tactic_description: string
  pillar_name: string
  supporting_evidence: string[]
  question_type: QuickAskQuestionType
  custom_question?: string
}

export interface QuickAskResponse {
  id: string
  tactic_id: string
  question_type: QuickAskQuestionType
  question_text: string
  response_text: string
  timestamp: string
  helpful?: boolean
}

// Human-readable question labels
export const QUESTION_LABELS: Record<QuickAskQuestionType, string> = {
  why: 'Why this recommendation?',
  how: 'How to implement?',
  risks: 'What are the risks?',
  resources: 'What resources are needed?',
  timeline: 'Expected timeline?',
  custom: 'Custom question...'
}

// Question descriptions for tooltips
export const QUESTION_DESCRIPTIONS: Record<QuickAskQuestionType, string> = {
  why: 'Understand the strategic logic and evidence behind this recommendation',
  how: 'Get actionable implementation steps and guidance',
  risks: 'Identify potential challenges and mitigation strategies',
  resources: 'Learn about budget, people, and tools needed',
  timeline: 'Get realistic duration and milestone estimates',
  custom: 'Ask your own specific question'
}

/**
 * Build a contextual prompt for the Quick Ask question
 */
export function buildQuickAskPrompt(context: QuickAskContext): string {
  const evidenceList = context.supporting_evidence.length > 0
    ? context.supporting_evidence.join(', ')
    : 'No direct graph evidence (domain expertise applied)'

  const baseContext = `
Context: The user is reviewing a MECE Scenario Analysis recommendation.

Tactic: ${context.tactic_title}
Pillar: ${context.pillar_name}
Description: ${context.tactic_description}
Supporting Evidence (Graph Nodes): ${evidenceList}
`

  const questionPrompts: Record<QuickAskQuestionType, string> = {
    why: `
${baseContext}

Question: Why is this recommendation appropriate?

Provide a focused explanation (2-3 paragraphs) that:
1. References the supporting evidence nodes if available
2. Explains the strategic logic connecting this tactic to the pillar
3. Connects to the company's specific situation
4. Highlights the expected business impact
`,

    how: `
${baseContext}

Question: How should this recommendation be implemented?

Provide actionable guidance including:
1. 3-5 concrete implementation steps in order
2. Key success factors to ensure effectiveness
3. Potential quick wins for early momentum
4. Dependencies to consider before starting
`,

    risks: `
${baseContext}

Question: What are the risks and challenges of implementing this?

Identify:
1. Top 3-5 risks (both internal and external)
2. Likelihood and potential impact for each
3. Practical mitigation strategies
4. Warning signs to monitor during implementation
`,

    resources: `
${baseContext}

Question: What resources are needed to implement this?

Outline requirements for:
1. People - roles, skills, estimated FTE
2. Budget - rough cost ranges if estimable
3. Tools/Technology - systems and platforms needed
4. External support - consultants, vendors, partners
`,

    timeline: `
${baseContext}

Question: What is a realistic implementation timeline?

Provide:
1. Overall duration estimate (range)
2. Key phases and milestones
3. Critical dependencies affecting timing
4. Factors that could accelerate or delay progress
`,

    custom: `
${baseContext}

User Question: ${context.custom_question || ''}

Provide a focused, helpful answer that directly addresses the question.
Reference the tactic context and supporting evidence where relevant.
`
  }

  return questionPrompts[context.question_type]
}

/**
 * Get the display text for a question type
 */
export function getQuestionText(
  questionType: QuickAskQuestionType,
  tacticTitle: string,
  customQuestion?: string
): string {
  if (questionType === 'custom' && customQuestion) {
    return customQuestion
  }

  const questionTemplates: Record<QuickAskQuestionType, string> = {
    why: `Why is "${tacticTitle}" recommended?`,
    how: `How should I implement "${tacticTitle}"?`,
    risks: `What are the risks of "${tacticTitle}"?`,
    resources: `What resources are needed for "${tacticTitle}"?`,
    timeline: `What is the timeline for "${tacticTitle}"?`,
    custom: customQuestion || ''
  }

  return questionTemplates[questionType]
}

/**
 * Generate a unique ID for a Quick Ask response
 */
export function generateQuickAskId(): string {
  return `qa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}
